import {
	CARD_TYPE,
	CAROUSEL_TYPE,
	REPLY_BUTTON
} from './components/constants'

export const getReplyOptions = (rbmFlow, rbmFlowAliases, flowId) => {
  const otherFlows = { ...rbmFlow }
  delete otherFlows[flowId]
  delete otherFlows['init']
  return Object.keys(otherFlows).map((flow) => {
    // Usar alias si existe, sino extraer nombre del flowId (ej: "dniCheck-1/1" -> "dniCheck")
    let label = rbmFlowAliases?.[flow]
    if (!label) {
      // Intentar extraer nombre del flow
      const flowName = flow.split('-')[0]
      label = flowName || flow
    }
    return {
      value: flow,
      label: label
    }
  })
}

/**
 * Builds a flow graph from rbmFlows for visualization
 * @param {Object} rbmFlows - Object with flow arrays keyed by flowId
 * @param {Object} rbmFlowAliases - Object mapping flowId to display names
 * @returns {Object} { nodes: [], edges: [] }
 */
export const buildFlowGraph = (rbmFlows, rbmFlowAliases = {}) => {
  const nodes = []
  const edges = []

  // Guard against undefined/null rbmFlows
  if (!rbmFlows || typeof rbmFlows !== 'object') {
    return { nodes, edges }
  }

  // Helper to get display name for a flow
  const getFlowDisplayName = (flowId) => {
    if (rbmFlowAliases[flowId]) return rbmFlowAliases[flowId]
    if (flowId === 'init') return 'Initial Communication'
    return flowId
  }

  // Helper to get first message preview from flow
  const getFlowPreview = (flowElements) => {
    if (!flowElements || flowElements.length === 0) return ''
    const firstElement = flowElements[0]
    if (firstElement?.text) {
      return firstElement.text.substring(0, 50) + (firstElement.text.length > 50 ? '...' : '')
    }
    return ''
  }

  // Helper to extract reply connections from buttons
  const extractRepliesFromButtons = (buttons, sourceFlowId) => {
    if (!buttons) return []
    return buttons
      .filter((btn) => btn.type === REPLY_BUTTON && btn.replyFlow)
      .map((btn) => ({
        from: sourceFlowId,
        to: btn.replyFlow,
        label: btn.text
      }))
  }

  // Helper to extract all connections from a flow element
  const extractConnectionsFromElement = (element, sourceFlowId) => {
    const connections = []

    // Check suggestions on the element
    if (element.suggestions) {
      connections.push(...extractRepliesFromButtons(element.suggestions, sourceFlowId))
    }

    // Check buttons on cards
    if (element.type === CARD_TYPE && element.buttons) {
      connections.push(...extractRepliesFromButtons(element.buttons, sourceFlowId))
    }

    // Check buttons on carousel cards
    if (element.type === CAROUSEL_TYPE && element.cards) {
      element.cards.forEach((card) => {
        if (card.buttons) {
          connections.push(...extractRepliesFromButtons(card.buttons, sourceFlowId))
        }
      })
    }

    return connections
  }

  // Build nodes and edges
  Object.keys(rbmFlows).forEach((flowId) => {
    if (flowId === 'rbmFlowAliasesMap') return

    const flowElements = rbmFlows[flowId]

    // Create node for this flow
    nodes.push({
      id: flowId,
      label: getFlowDisplayName(flowId),
      preview: getFlowPreview(flowElements),
      elementCount: flowElements?.length || 0,
      isInit: flowId === 'init'
    })

    // Extract edges from all elements in this flow
    if (flowElements && Array.isArray(flowElements)) {
      flowElements.forEach((element) => {
        if (!element) return // Skip undefined/null elements
        const connections = extractConnectionsFromElement(element, flowId)
        edges.push(...connections)
      })
    }
  })

  // Sort nodes: init first, then alphabetically
  nodes.sort((a, b) => {
    if (a.isInit) return -1
    if (b.isInit) return 1
    return a.label.localeCompare(b.label)
  })

  return { nodes, edges }
}

/**
 * Gets all elements for a specific flow from rbmFlows
 * @param {Object} rbmFlows - Object with flow arrays
 * @param {string} flowId - The flow to get
 * @returns {Array} Array of elements in the flow
 */
export const getFlowElements = (rbmFlows, flowId) => {
  return rbmFlows[flowId] || []
}
